import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Alert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import { dayOf } from '../utils/dates';
import { useI18n } from '../i18n';
import { useCan } from '../hooks/useMe';
import api from '../services/api';

// «Próximo» del diseño: la barra de color a la izquierda indica urgencia —
// rosa si ya venció, ocre si vence esta semana, mauve el resto.
function urgencyColor(task) {
  if (task.done_at) return '#4F7A5F';
  if (!task.due_date) return '#847782';
  const days = (dayOf(task.due_date) - Date.now()) / 86400000;
  if (days < 0) return '#AE5B61';
  if (days <= 7) return '#C9873F';
  return '#847782';
}

export default function AccountTasks({ tenantId, source, owners = [], onChanged }) {
  const { t, fmtDate } = useI18n();
  const canLog = useCan('log_activity');

  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [draft, setDraft] = useState(null);

  const load = useCallback(() => {
    api.get(`/api/accounts/${tenantId}/tasks`, { params: { source } })
      .then((res) => setTasks(res.data))
      .catch(() => setError(t('tasks.loadError')));
  }, [tenantId, source, t]);

  useEffect(() => { load(); }, [load]);

  async function toggle(task) {
    setBusy(task.id);
    try {
      await api.patch(`/api/tasks/${task.id}`, { done: !task.done_at });
      load();
      if (onChanged) onChanged();
    } catch {
      setError(t('tasks.saveError'));
    } finally {
      setBusy(null);
    }
  }

  async function create() {
    if (!draft?.title?.trim()) return;
    setBusy('new');
    try {
      await api.post(`/api/accounts/${tenantId}/tasks`, {
        title: draft.title.trim(),
        due_date: draft.due_date || null,
        assignee_id: draft.assignee_id || null,
      }, { params: { source } });
      setDraft(null);
      load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || t('tasks.saveError'));
    } finally {
      setBusy(null);
    }
  }

  async function remove(task) {
    if (!window.confirm(t('tasks.deleteConfirm'))) return;
    setBusy(task.id);
    try {
      await api.delete(`/api/tasks/${task.id}`);
      load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || t('tasks.deleteError'));
    } finally {
      setBusy(null);
    }
  }

  const open = tasks.filter((x) => !x.done_at);
  const done = tasks.filter((x) => x.done_at);

  return (
    <Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: 1.2,
        fontSize: 12, fontWeight: 600, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: '#847782',
      }}>
        {t('tasks.title')}
        {open.length > 0 && (
          <Box component="span" sx={{ color: '#FF6666', fontSize: 13 }}>{open.length}</Box>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {canLog && !draft && (
          <Button size="small" onClick={() => setDraft({ title: '', due_date: '', assignee_id: '' })}
            sx={{ fontSize: 11.5, color: '#FF6666', textTransform: 'none', letterSpacing: 0 }}>
            {t('tasks.add')}
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

      {draft && (
        <Box sx={{ mb: 2, display: 'grid', gap: 1 }}>
          <TextField size="small" autoFocus placeholder={t('tasks.placeholder')}
            value={draft.title} disabled={busy === 'new'}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" type="date" sx={{ flexGrow: 1 }}
              value={draft.due_date} disabled={busy === 'new'}
              onChange={(e) => setDraft({ ...draft, due_date: e.target.value })} />
            <TextField select size="small" sx={{ minWidth: 130 }}
              value={draft.assignee_id} disabled={busy === 'new'}
              onChange={(e) => setDraft({ ...draft, assignee_id: e.target.value })}>
              <MenuItem value="">{t('tasks.assignToMe')}</MenuItem>
              {owners.map((o) => (
                <MenuItem key={o.id} value={o.id} sx={{ fontSize: 13.5 }}>{o.display_name}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="contained" disabled={busy === 'new' || !draft.title.trim()}
              onClick={create}>{t('common.save')}</Button>
            <Button size="small" onClick={() => setDraft(null)} sx={{ color: '#847782' }}>
              {t('common.close')}
            </Button>
          </Box>
        </Box>
      )}

      {tasks.length === 0 && !draft && (
        <Typography sx={{ fontSize: 13.5, color: '#847782' }}>{t('tasks.empty')}</Typography>
      )}

      {[...open, ...done].map((task) => (
        <Box key={task.id} sx={{
          display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.8,
          borderBottom: '1px solid #F5F1F3', '&:last-of-type': { borderBottom: 'none' },
        }}>
          <Box sx={{
            width: 3, alignSelf: 'stretch', borderRadius: '2px', flexShrink: 0,
            backgroundColor: urgencyColor(task),
          }} />
          <Checkbox
            size="small" checked={Boolean(task.done_at)} disabled={busy === task.id || !canLog}
            onChange={() => toggle(task)} sx={{ p: 0.4, mt: -0.2 }}
          />
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography sx={{
              fontSize: 13.5, fontWeight: 500,
              textDecoration: task.done_at ? 'line-through' : 'none',
              color: task.done_at ? '#847782' : 'inherit',
            }}>{task.title}</Typography>
            <Typography sx={{ fontSize: 11.5, color: '#847782' }}>
              {task.due_date ? `${t('tasks.due')} ${fmtDate(dayOf(task.due_date))}` : t('tasks.noDate')}
              {task.assignee_name ? ` · ${task.assignee_name}` : ''}
            </Typography>
          </Box>
          {canLog && (
            <Box
              component="button"
              onClick={() => remove(task)}
              disabled={busy === task.id}
              title={t('common.delete')}
              sx={{
                border: 'none', background: 'none', cursor: 'pointer', p: 0, mt: 0.3,
                color: '#847782', fontSize: 15, lineHeight: 1, fontFamily: 'inherit',
                '&:hover': { color: '#AE5B61' },
              }}
            >×</Box>
          )}
        </Box>
      ))}
    </Box>
  );
}
